/**
 * TrainingDetail — full-screen page for a single training module.
 *
 * Staff view: read the markdown body, download attached media, mark
 * their own assignment complete, self-start when there's no
 * pre-existing assignment.
 *
 * Manager view: edit body/description, upload/delete media, assign to
 * staff (with due date), mark staff competent (with note), and see who
 * is currently competent on this module.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, downloadUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, PlaySquare, Loader2, Edit3, Send, Archive, Award, Upload,
  Trash2, CheckCircle2, Users as UsersIcon, Download, FileText, Video, Image as ImageIcon,
  Eye, Play,
} from "lucide-react";


export default function TrainingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);

  const [mod, setMod] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showSignoff, setShowSignoff] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rMod, rAssign, rComp, rUsers] = await Promise.all([
        api.get(`/training/modules/${id}`),
        api.get("/training/assignments", { params: { mine: true, module_id: id } }),
        api.get(`/training/modules/${id}/competencies`),
        isManager ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setMod(rMod.data);
      setAssignment((rAssign.data || [])[0] || null);
      setCompetencies(rComp.data || []);
      setUsers(rUsers.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [id, isManager]);

  useEffect(() => { load(); }, [load]);

  const myCompetency = useMemo(
    () => competencies.find((c) => c.user_id === user?.id) || null,
    [competencies, user?.id],
  );

  async function selfStart() {
    try {
      const r = await api.post(`/training/modules/${id}/self-start`, {});
      setAssignment(r.data);
      toast.success("Marked in progress");
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function markComplete() {
    if (!assignment) return;
    try {
      await api.post(`/training/assignments/${assignment.id}/status`, { status: "completed" });
      toast.success("Marked complete — ask your manager for a competency sign-off.");
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function archive() {
    if (!window.confirm("Archive this training module?")) return;
    try {
      await api.delete(`/training/modules/${id}`);
      toast.success("Archived");
      nav("/learning?tab=training");
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function onFilePicked(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/training/modules/${id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
    } finally { setUploading(false); }
  }

  async function deleteMedia(mediaId) {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/training/modules/${id}/media/${mediaId}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!mod) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          Module not found.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid={`training-detail-${id}`}>
      <div>
        <Link
          to="/learning?tab=training"
          className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
          data-testid="training-back"
        >
          <ArrowLeft className="h-3 w-3" /> Back to training
        </Link>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <PlaySquare className="h-6 w-6" /> {mod.title}
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-zinc-500">
            {mod.category && (
              <span className="uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 text-2xs">
                {mod.category}
              </span>
            )}
            <span>{(mod.media || []).length} attachment{(mod.media || []).length === 1 ? "" : "s"}</span>
          </div>
          {mod.description && (
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">{mod.description}</p>
          )}
        </div>
        {isManager && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowEdit(true)} className="text-xs" data-testid="training-edit-btn">
              <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit content
            </Button>
            <Button onClick={() => setShowAssign(true)} className="bg-zinc-950 text-white text-xs" data-testid="training-assign-btn">
              <Send className="h-3.5 w-3.5 mr-1" /> Assign
            </Button>
            <Button onClick={() => setShowSignoff(true)} variant="outline" className="text-xs" data-testid="training-signoff-btn">
              <Award className="h-3.5 w-3.5 mr-1" /> Sign off competency
            </Button>
            <Button
              variant="outline"
              onClick={archive}
              className="text-xs text-red-700 hover:bg-red-50"
              data-testid="training-archive-btn"
            >
              <Archive className="h-3.5 w-3.5 mr-1" /> Archive
            </Button>
          </div>
        )}
      </div>

      {/* Assignment status banner */}
      <Card className="p-4 rounded-md shadow-none border border-zinc-200">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            {myCompetency ? (
              <span className="text-emerald-800 flex items-center gap-1.5">
                <Award className="h-4 w-4" />
                Competency signed off by <b>{myCompetency.signed_by_name}</b> on{" "}
                <b>{new Date(myCompetency.signed_at).toLocaleDateString()}</b>
                {myCompetency.note && <span className="text-zinc-600 italic ml-1">— {myCompetency.note}</span>}
              </span>
            ) : assignment?.status === "completed" ? (
              <span className="text-zinc-700 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                You marked this complete. Waiting on a manager sign-off.
              </span>
            ) : assignment ? (
              <span className="text-amber-900">
                Assigned {assignment.assigned_at && `on ${new Date(assignment.assigned_at).toLocaleDateString()}`}.
                {assignment.due_at && (
                  <> Due <b>{new Date(assignment.due_at).toLocaleDateString()}</b>.</>
                )}
              </span>
            ) : (
              <span className="text-zinc-600">
                No assignment yet — start whenever you&apos;re ready.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!assignment && !myCompetency && (
              <Button onClick={selfStart} data-testid="training-self-start-btn" className="bg-zinc-950 text-white">
                <Play className="h-4 w-4 mr-1" /> Start module
              </Button>
            )}
            {assignment && assignment.status !== "completed" && !myCompetency && (
              <Button
                onClick={markComplete}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="training-mark-complete-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> I&apos;ve finished
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Body */}
      <Card className="p-6 rounded-md shadow-none border border-zinc-200" data-testid="training-body">
        {mod.body_markdown ? (
          <div className="prose prose-sm max-w-none prose-headings:text-zinc-950 prose-p:text-zinc-800 prose-li:text-zinc-800 prose-strong:text-zinc-950 prose-a:text-blue-700">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{mod.body_markdown}</ReactMarkdown>
          </div>
        ) : (
          <div className="text-sm text-zinc-500 italic">
            No content yet. {isManager && "Click \"Edit content\" to add the module body."}
          </div>
        )}
      </Card>

      {/* Media */}
      <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="training-media">
        <div className="flex items-center justify-between mb-3">
          <div className="text-2xs uppercase tracking-wider text-zinc-500">Attachments</div>
          {isManager && (
            <>
              <input
                ref={fileRef}
                type="file"
                onChange={onFilePicked}
                className="hidden"
                data-testid="training-media-input"
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="text-xs"
                data-testid="training-media-upload-btn"
              >
                {uploading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…</> : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload PDF / video / image</>}
              </Button>
            </>
          )}
        </div>
        {(mod.media || []).length === 0 ? (
          <div className="text-sm text-zinc-500 italic">No attachments.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {mod.media.map((m) => (
              <MediaRow key={m.id} media={m} moduleId={id} isManager={isManager} onDelete={() => deleteMedia(m.id)} />
            ))}
          </ul>
        )}
      </Card>

      {/* Competencies (manager view) */}
      {isManager && (
        <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="training-competencies">
          <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 mb-2">
            <UsersIcon className="h-3.5 w-3.5" /> Competency sign-offs ({competencies.length})
          </div>
          {competencies.length === 0 ? (
            <div className="text-sm text-zinc-500 italic">Nobody has been signed off yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {competencies.map((c) => (
                <li key={c.id} className="text-sm flex items-start gap-2" data-testid={`competency-${c.id}`}>
                  <Award className="h-3.5 w-3.5 text-emerald-700 mt-0.5 shrink-0" />
                  <div>
                    <div>
                      <b>{c.user_name || users.find((u) => u.id === c.user_id)?.full_name || c.user_id}</b>
                      <span className="text-xs text-zinc-500 ml-2">
                        signed by {c.signed_by_name} · {new Date(c.signed_at).toLocaleString()}
                      </span>
                    </div>
                    {c.note && <div className="text-xs text-zinc-600 italic">{c.note}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {showEdit && <EditModuleDialog mod={mod} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
      {showAssign && <AssignDialog moduleId={id} users={users} onClose={() => setShowAssign(false)} onSaved={() => { setShowAssign(false); load(); }} />}
      {showSignoff && <SignOffDialog moduleId={id} users={users} existing={competencies} onClose={() => setShowSignoff(false)} onSaved={() => { setShowSignoff(false); load(); }} />}
    </div>
  );
}


function MediaRow({ media, moduleId, isManager, onDelete }) {
  const Icon = media.content_type?.startsWith("video/") ? Video
    : media.content_type?.startsWith("image/") ? ImageIcon
    : FileText;
  const url = downloadUrl(`/training/modules/${moduleId}/media/${media.id}/download`);
  const size = media.size_bytes;
  const sizeTxt = size < 1024*1024 ? `${(size/1024).toFixed(1)} KB` : `${(size/1024/1024).toFixed(1)} MB`;
  const canPreview = media.content_type?.startsWith("image/") || media.content_type?.startsWith("video/") || media.content_type === "application/pdf";
  return (
    <li className="py-2 flex items-center gap-2 flex-wrap" data-testid={`media-row-${media.id}`}>
      <Icon className="h-4 w-4 text-zinc-500 shrink-0" />
      <span className="text-sm text-zinc-800 flex-1 min-w-0 truncate">{media.filename}</span>
      <span className="text-2xs text-zinc-500 font-mono-num shrink-0">{sizeTxt}</span>
      {canPreview && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
          data-testid={`media-preview-${media.id}`}
        >
          <Eye className="h-3 w-3" /> Preview
        </a>
      )}
      <a
        href={url}
        download={media.filename}
        className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
        data-testid={`media-download-${media.id}`}
      >
        <Download className="h-3 w-3" /> Download
      </a>
      {isManager && (
        <button
          onClick={onDelete}
          className="text-xs text-red-700 hover:bg-red-50 inline-flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-md"
          data-testid={`media-delete-${media.id}`}
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      )}
    </li>
  );
}


function EditModuleDialog({ mod, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: mod.title || "",
    category: mod.category || "",
    description: mod.description || "",
    body_markdown: mod.body_markdown || "",
    competency_renewal_months: mod.competency_renewal_months ?? "",
  });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    const renewalRaw = String(form.competency_renewal_months ?? "").trim();
    let renewal = null;
    if (renewalRaw) {
      const n = parseInt(renewalRaw, 10);
      if (!Number.isFinite(n) || n < 1 || n > 120) {
        return toast.error("Renewal must be a whole number between 1 and 120 months");
      }
      renewal = n;
    }
    setSaving(true);
    try {
      await api.patch(`/training/modules/${mod.id}`, {
        title: form.title.trim(),
        category: form.category.trim() || null,
        description: form.description,
        body_markdown: form.body_markdown,
        competency_renewal_months: renewal,
      });
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="edit-training-dialog">
        <DialogHeader>
          <DialogTitle>Edit training module</DialogTitle>
          <DialogDescription className="text-xs">
            Markdown supported in the body. Use <b>Upload</b> in the module page
            to attach PDFs, videos or images.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-training-title" />
            </div>
            <div>
              <Label className="text-2xs">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-training-category" />
            </div>
          </div>
          <div>
            <Label className="text-2xs">Short description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-training-description" />
          </div>
          <div>
            <Label className="text-2xs">
              Competency renewal (months)
              <span className="text-zinc-400 font-normal ml-1">· leave blank for no expiry</span>
            </Label>
            <Input
              type="number"
              min="1"
              max="120"
              placeholder="e.g. 12 for annual re-sign-off"
              value={form.competency_renewal_months}
              onChange={(e) => setForm({ ...form, competency_renewal_months: e.target.value })}
              className="max-w-[220px]"
              data-testid="edit-training-renewal-months"
            />
            <div className="text-2xs text-zinc-500 mt-0.5">
              After this many months the staff member&apos;s sign-off is flagged as <b>Lapsed</b> on the Team overview.
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-2xs">Body (markdown)</Label>
            <button
              onClick={() => setPreview((v) => !v)}
              className="text-xs text-zinc-600 hover:text-zinc-900 underline"
              data-testid="edit-training-preview-toggle"
            >
              {preview ? "Edit" : "Preview"}
            </button>
          </div>
          {preview ? (
            <div className="min-h-[240px] border border-zinc-200 rounded-md p-3 bg-zinc-50 max-h-[420px] overflow-y-auto prose prose-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body_markdown || "_(empty)_"}</ReactMarkdown>
            </div>
          ) : (
            <Textarea
              rows={12}
              value={form.body_markdown}
              onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
              className="font-mono-num text-sm"
              data-testid="edit-training-body"
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="edit-training-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AssignDialog({ moduleId, users, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);

  const staff = useMemo(() => users.filter((u) => !u.disabled), [users]);

  async function save() {
    if (selected.length === 0) return toast.error("Pick at least one staff member");
    setSaving(true);
    try {
      await api.post(`/training/modules/${moduleId}/assign`, {
        user_ids: selected,
        due_at: due ? `${due}T09:00:00Z` : null,
      });
      toast.success(`Assigned to ${selected.length} staff`);
      onSaved?.();
    } catch (e) {
      toast.error("Assign failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assign-training-dialog">
        <DialogHeader>
          <DialogTitle>Assign training</DialogTitle>
          <DialogDescription className="text-xs">
            Each assignee gets a Resend email + in-app notification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Staff (Cmd/Ctrl-click for multiple)</Label>
            <select
              multiple
              value={selected}
              onChange={(e) => setSelected(Array.from(e.target.selectedOptions).map((o) => o.value))}
              className="mt-1 min-h-[140px] px-2 py-1 border border-zinc-300 rounded-md text-sm bg-white w-full"
              data-testid="assign-training-users"
            >
              {staff.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.email} · {u.role}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-2xs">Due date (optional)</Label>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="assign-training-due" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="assign-training-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Assigning…</> : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SignOffDialog({ moduleId, users, existing, onClose, onSaved }) {
  const alreadySignedIds = useMemo(() => new Set(existing.map((c) => c.user_id)), [existing]);
  const [userId, setUserId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const staff = useMemo(
    () => users.filter((u) => !u.disabled && !alreadySignedIds.has(u.id)),
    [users, alreadySignedIds],
  );

  async function save() {
    if (!userId) return toast.error("Pick a staff member");
    setSaving(true);
    try {
      await api.post(`/training/modules/${moduleId}/competencies`, { user_id: userId, note });
      toast.success("Competency signed off");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="signoff-training-dialog">
        <DialogHeader>
          <DialogTitle>Sign off competency</DialogTitle>
          <DialogDescription className="text-xs">
            Records a permanent, dated sign-off for this staff member on this module.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Staff member</Label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="mt-1 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white w-full"
              data-testid="signoff-training-user"
            >
              <option value="">Select…</option>
              {staff.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.email} · {u.role}</option>
              ))}
            </select>
            {staff.length === 0 && (
              <p className="text-2xs text-zinc-500 mt-1">
                Every non-disabled staff member is already signed off for this module.
              </p>
            )}
          </div>
          <div>
            <Label className="text-2xs">Note (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Demonstrated technique correctly during in-person review."
              data-testid="signoff-training-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="signoff-training-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Signing…</> : "Sign off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
